<template>
  <div class="teacher-evaluation">
    <!-- 教学评价维度分析 -->
    <div class="evaluation-radar chart-item">
      <div class="chart-header">
        <span class="chart-title">教学评价维度分析</span>
        <div class="total-info">
          <span class="total-value">{{ averageScore.toFixed(1) }}</span>
          <span class="total-label">平均评分</span>
        </div>
      </div>
      <div class="chart-container">
        <Echart :options="radarOptions" height="100%" />
      </div>
    </div>

    <!-- 教学效果分析 -->
    <div class="effectiveness-chart chart-item">
      <div class="chart-header">
        <span class="chart-title">教学效果分析</span>
        <div class="total-info">
          <span class="total-value">{{ effectivenessScore.toFixed(1) }}</span>
          <span class="total-label">效果评分</span>
        </div>
      </div>
      <div class="chart-container">
        <Echart :options="effectivenessOptions" height="100%" />
      </div>
    </div>

    <!-- 评教统计 -->
    <div class="evaluation-stats chart-item">
      <div class="chart-header">
        <span class="chart-title">评教统计</span>
      </div>
      <div class="stat-cards">
        <div class="stat-card" v-for="(item, index) in statsData" :key="index">
          <div class="stat-value">{{ item.value }}%</div>
          <div class="stat-label">{{ item.label }}</div>
          <div class="progress-bar">
            <div class="progress" :style="{ width: item.value + '%' }"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Echart from '@/common/echart'
import analysisData from '@/mock/teacher/analysis.json'

export default {
  name: 'TeacherEvaluation',
  components: {
    Echart
  },
  data() {
    // 默认评分数据
    const defaultData = {
      categories: ['教学方法', '教学内容', '课堂氛围', '互动质量', '作业反馈'],
      scores: [4.5, 4.8, 4.2, 4.6, 4.3]
    };

    const scores = defaultData.scores;
    const averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    const effectivenessScore = analysisData.courses[0].teachingEffectiveness.teachingMethods
      .reduce((a, b) => a + b.effectivenessScore, 0) / 
      analysisData.courses[0].teachingEffectiveness.teachingMethods.length;

    return {
      chartHeight: '18vh',
      averageScore,
      effectivenessScore,
      // 教学评价维度分析配置
      radarOptions: {
        tooltip: {
          trigger: 'item'
        },
        radar: {
          radius: '60%',
          center: ['50%', '50%'],
          indicator: defaultData.categories.map(category => ({
            name: category,
            max: 5
          })),
          splitArea: {
            areaStyle: {
              color: ['rgba(255,255,255,0.02)', 'rgba(255,255,255,0.05)']
            }
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(255,255,255,0.2)'
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(255,255,255,0.2)'
            }
          },
          name: {
            textStyle: {
              color: '#fff'
            }
          }
        },
        series: [
          {
            type: 'radar',
            data: [
              {
                value: scores,
                name: '评价得分',
                lineStyle: {
                  color: '#37a2da',
                  width: 2
                },
                areaStyle: {
                  color: new this.$echarts.graphic.RadialGradient(0.5, 0.5, 1, [
                    { offset: 0, color: 'rgba(55,162,218,0.5)' },
                    { offset: 1, color: 'rgba(55,162,218,0.1)' }
                  ])
                },
                symbolSize: 6,
                itemStyle: {
                  color: '#37a2da'
                }
              }
            ]
          }
        ]
      },

      // 教学效果分析配置
      effectivenessOptions: {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          top: '15%',
          right: '5%',
          bottom: '12%',
          left: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: analysisData.courses[0].teachingEffectiveness.teachingMethods.map(item => item.method),
          axisLabel: {
            color: '#fff',
            interval: 0,
            rotate: 30
          },
          axisLine: {
            lineStyle: { color: 'rgba(255,255,255,0.2)' }
          }
        },
        yAxis: {
          type: 'value',
          max: 5,
          axisLabel: { color: '#fff' },
          axisLine: {
            lineStyle: { color: 'rgba(255,255,255,0.2)' }
          },
          splitLine: {
            lineStyle: { color: 'rgba(255,255,255,0.1)' }
          }
        },
        series: [
          {
            type: 'bar',
            data: analysisData.courses[0].teachingEffectiveness.teachingMethods.map(item => ({
              value: item.effectivenessScore,
              itemStyle: {
                color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#67e0e3' },
                  { offset: 1, color: '#37a2da' }
                ])
              }
            })),
            barWidth: '40%',
            label: {
              show: true,
              position: 'top',
              color: '#fff',
              formatter: '{c}'
            }
          }
        ]
      },

      // 评教统计数据
      statsData: [
        {
          label: '优秀评价',
          value: analysisData.courses[0].teachingEffectiveness.studentFeedback.positive
        },
        {
          label: '良好评价',
          value: analysisData.courses[0].teachingEffectiveness.studentFeedback.neutral
        },
        {
          label: '待改进',
          value: analysisData.courses[0].teachingEffectiveness.studentFeedback.negative
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
.teacher-evaluation {
  padding: 8px;
  height: 100%;
  display: grid;
  grid-template-rows: 30% 30% 40%;
  gap: 8px;

  .chart-item {
    background: rgba(6, 30, 93, 0.5);
    border-radius: 4px;
    padding: 8px;
    backdrop-filter: blur(4px);
    box-shadow: 0 0 10px rgba(0,0,0,0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .chart-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 4px;
      flex-shrink: 0;
      height: 28px;

      .chart-title {
        color: #fff;
        font-size: 14px;
        font-weight: 500;
        &::before {
          content: '';
          display: inline-block;
          width: 3px;
          height: 14px;
          background: #67e0e3;
          margin-right: 6px;
          vertical-align: middle;
          border-radius: 2px;
        }
      }

      .total-info {
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        .total-value {
          font-size: 18px;
          color: #67e0e3;
          font-weight: bold;
        }

        .total-label {
          font-size: 12px;
          color: #fff;
          opacity: 0.8;
        }
      }
    }

    .chart-container {
      flex: 1;
      min-height: 0;
      height: calc(100% - 32px);
      position: relative;

      :deep(.echarts) {
        width: 100% !important;
        height: 100% !important;
      }
    }
  }

  .stat-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding: 5px;
    height: calc(100% - 37px);

    .stat-card {
      background: rgba(6, 30, 93, 0.7);
      border-radius: 8px;
      padding: 10px;
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .stat-value {
        font-size: 20px;
        color: #67e0e3;
        font-weight: bold;
        margin-bottom: 4px;
      }

      .stat-label {
        color: #fff;
        font-size: 12px;
        margin-bottom: 8px;
      }

      .progress-bar {
        width: 100%;
        height: 4px;
        background: rgba(255,255,255,0.1);
        border-radius: 2px;
        overflow: hidden;

        .progress {
          height: 100%;
          background: linear-gradient(to right, #67e0e3, #4169e1);
          border-radius: 2px;
          transition: width 0.3s ease;
        }
      }
    }
  }
}
</style> 